import { Head, useForm, router } from '@inertiajs/react';
import AppLayout from '@/Layouts/AppLayout';
import Card, { CardBody } from '@/Components/Card';
import Button from '@/Components/Button';
import FormInput from '@/Components/FormInput';
import PageHeader from '@/Components/PageHeader';
import Alert from '@/Components/Alert';
import { UserCircleIcon, LockClosedIcon, EnvelopeIcon, UserIcon } from '@heroicons/react/24/outline';

export default function Edit({ user, flash, status }) {
    const profileForm = useForm({ name: user.name || '', email: user.email || '' });
    const passwordForm = useForm({ current_password: '', password: '', password_confirmation: '' });
    const deleteForm = useForm({ password: '' });

    const updateProfile = (e) => { e.preventDefault(); profileForm.patch('/profile'); };
    const updatePassword = (e) => { e.preventDefault(); passwordForm.put('/password', { onSuccess: () => passwordForm.reset() }); };
    const deleteAccount = (e) => {
        e.preventDefault();
        if (confirm('Are you sure you want to delete your account? This action cannot be undone.')) {
            deleteForm.delete('/profile');
        }
    };

    return (
        <AppLayout>
            <Head title="Profile Settings" />
            <PageHeader title="Profile Settings" subtitle="Manage your account details" />
            {flash?.success && <Alert type="success" className="mb-4">{flash.success}</Alert>}
            {status === 'profile-updated' && <Alert type="success" className="mb-4">Profile updated successfully.</Alert>}

            {/* Profile Info */}
            <Card className="mb-6">
                <div className="px-6 py-4 border-b border-slate-100 flex items-center gap-3">
                    <UserCircleIcon className="w-5 h-5 text-emerald-700" />
                    <h3 className="font-semibold text-slate-800">Profile Information</h3>
                </div>
                <CardBody>
                    <form onSubmit={updateProfile} className="space-y-4 max-w-lg">
                        <FormInput label="Name" icon={UserIcon} value={profileForm.data.name} onChange={e => profileForm.setData('name', e.target.value)} error={profileForm.errors.name} required />
                        <FormInput label="Email" type="email" icon={EnvelopeIcon} value={profileForm.data.email} onChange={e => profileForm.setData('email', e.target.value)} error={profileForm.errors.email} required />
                        <div className="flex gap-3 pt-2"><Button type="submit" disabled={profileForm.processing}>Save Profile</Button></div>
                    </form>
                </CardBody>
            </Card>

            {/* Password */}
            <Card className="mb-6">
                <div className="px-6 py-4 border-b border-slate-100 flex items-center gap-3">
                    <LockClosedIcon className="w-5 h-5 text-emerald-700" />
                    <h3 className="font-semibold text-slate-800">Change Password</h3>
                </div>
                <CardBody>
                    <form onSubmit={updatePassword} className="space-y-4 max-w-lg">
                        <FormInput label="Current Password" type="password" icon={LockClosedIcon} value={passwordForm.data.current_password} onChange={e => passwordForm.setData('current_password', e.target.value)} error={passwordForm.errors.current_password} required />
                        <FormInput label="New Password" type="password" icon={LockClosedIcon} value={passwordForm.data.password} onChange={e => passwordForm.setData('password', e.target.value)} error={passwordForm.errors.password} required />
                        <FormInput label="Confirm Password" type="password" icon={LockClosedIcon} value={passwordForm.data.password_confirmation} onChange={e => passwordForm.setData('password_confirmation', e.target.value)} error={passwordForm.errors.password_confirmation} required />
                        <div className="flex gap-3 pt-2"><Button type="submit" disabled={passwordForm.processing}>Update Password</Button></div>
                    </form>
                </CardBody>
            </Card>

            {/* Delete Account */}
            <Card>
                <div className="px-6 py-4 border-b border-red-100">
                    <h3 className="font-semibold text-red-600">Danger Zone</h3>
                </div>
                <CardBody>
                    <p className="text-sm text-slate-600 mb-4">Once your account is deleted, all of its resources and data will be permanently deleted.</p>
                    <form onSubmit={deleteAccount} className="space-y-4 max-w-lg">
                        <FormInput label="Confirm Password" type="password" icon={LockClosedIcon} value={deleteForm.data.password} onChange={e => deleteForm.setData('password', e.target.value)} error={deleteForm.errors.password} required placeholder="Enter your password to confirm" />
                        {deleteForm.errors.password && <p className="text-xs text-red-500">{deleteForm.errors.password}</p>}
                        <Button type="submit" variant="danger" size="sm" disabled={deleteForm.processing}>Delete Account</Button>
                    </form>
                </CardBody>
            </Card>
        </AppLayout>
    );
}
